// src/components/PuzzleScreen.jsx
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { getProgress, saveProgress } from '../utils/offline'
import ChickenExplosion from './ChickenExplosion'

/**
 * Final puzzle screen where kids arrange scrambled words
 * @param {Array} words - Correct words in order
 * @param {Function} onComplete - Callback when puzzle is solved
 * @param {string} hint - Final hint to show when solved
 * @param {boolean} initialCompleted - Whether puzzle starts in completed state
 */
export default function PuzzleScreen({ words, onComplete, hint, initialCompleted = false }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [scrambled, setScrambled] = useState([])
  const [selected, setSelected] = useState([])
  const [error, setError] = useState(null)
  const [completed, setCompleted] = useState(initialCompleted)
  const [showAnimation, setShowAnimation] = useState(false)

  // Shuffle words on mount
  useEffect(() => {
    const shuffled = [...words].sort(() => Math.random() - 0.5)
    setScrambled(shuffled)
  }, [words])

  function handleWordClick(word) {
    // Add to selected
    const newSelected = [...selected, word]
    setSelected(newSelected)

    // Remove from scrambled
    const newScrambled = [...scrambled]
    const index = newScrambled.indexOf(word)
    newScrambled.splice(index, 1)
    setScrambled(newScrambled)

    setError(null)
  }

  function handleUndo() {
    if (selected.length === 0) return

    const lastWord = selected[selected.length - 1]
    const newSelected = selected.slice(0, -1)
    setSelected(newSelected)

    const newScrambled = [lastWord, ...scrambled]
    setScrambled(newScrambled)
    setError(null)
  }

  function handleReset() {
    setSelected([])
    const shuffled = [...words].sort(() => Math.random() - 0.5)
    setScrambled(shuffled)
    setError(null)

    // If in completed state, reset progress and navigate to home
    if (completed) {
      setCompleted(false)
      // Reset puzzle completion in progress
      getProgress().then(async (prog) => {
        await saveProgress({
          ...prog,
          puzzleCompleted: false,
          puzzleCompletedAt: null,
          foundTreasures: []
        })
      }).catch(err => console.error('Failed to reset progress:', err))

      navigate('/')
    }
  }

  function handleSubmit() {
    const submittedSentence = selected.join(' ')
    const correctSentence = words.join(' ')

    if (submittedSentence === correctSentence) {
      setShowAnimation(true)
    } else {
      setError(t('puzzle.notCorrect', { defaultValue: 'Not quite! Try again.' }))
    }
  }

  if (completed || showAnimation) {
    return (
      <>
        {showAnimation && (
          <ChickenExplosion
            onAnimationComplete={() => {
              setShowAnimation(false)
              setCompleted(true)
              onComplete()
            }}
          />
        )}
        {completed && (
          <div style={styles.container}>
            <h1 style={{ color: '#28a745', marginBottom: '20px' }}>{t('puzzle.completed')}</h1>
            <p style={{ fontSize: '18px', lineHeight: '1.6', marginBottom: '30px' }}>
              <strong>{t('puzzle.finalHint')}</strong>
            </p>
            <p style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#FF6B6B',
              padding: '20px',
              background: '#FFF3E0',
              borderRadius: '8px',
              textAlign: 'center',
              marginBottom: '30px'
            }}>
              {hint}
            </p>
            <button
              onClick={handleReset}
              style={{
                ...styles.actionButton,
                background: '#28a745',
                padding: '15px 30px',
                fontSize: '16px'
              }}
            >
              {t('puzzle.startOver')}
            </button>
          </div>
        )}
      </>
    )
  }

  return (
    <div style={styles.container}>
      <h1>{t('puzzle.title')}</h1>
      <p style={{ color: '#666', marginBottom: '20px' }}>
        {t('puzzle.instructions')}
      </p>

      {/* Selected words (being arranged) */}
      <div style={styles.selectedBox}>
        {selected.length === 0 ? (
          <p style={{ color: '#999' }}>{t('puzzle.placeholder')}</p>
        ) : (
          <p style={{ fontSize: '20px', fontWeight: 'bold', margin: 0 }}>
            {selected.join(' ')}
          </p>
        )}
      </div>

      {/* Scrambled words to click */}
      <div style={styles.wordsContainer}>
        {scrambled.map((word, index) => (
          <button
            key={index}
            onClick={() => handleWordClick(word)}
            style={styles.wordButton}
          >
            {word}
          </button>
        ))}
      </div>

      {/* Error message */}
      {error && (
        <p style={{ color: 'red', fontSize: '14px', marginTop: '10px' }}>
          {error}
        </p>
      )}

      {/* Action buttons */}
      <div style={styles.buttonGroup}>
        <button
          onClick={handleUndo}
          disabled={selected.length === 0}
          style={{
            ...styles.actionButton,
            opacity: selected.length === 0 ? 0.5 : 1,
            cursor: selected.length === 0 ? 'not-allowed' : 'pointer'
          }}
        >
          {t('puzzle.undo')}
        </button>

        <button
          onClick={handleReset}
          style={styles.actionButton}
        >
          {t('puzzle.reset')}
        </button>

        <button
          onClick={handleSubmit}
          disabled={selected.length !== words.length}
          style={{
            ...styles.actionButton,
            background: '#28a745',
            opacity: selected.length === words.length ? 1 : 0.5,
            cursor: selected.length === words.length ? 'pointer' : 'not-allowed'
          }}
        >
          {t('puzzle.submit')}
        </button>
      </div>
    </div>
  )
}

const styles = {
  container: {
    maxWidth: '500px',
    margin: '0 auto',
    padding: '30px 20px',
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center'
  },
  selectedBox: {
    background: '#f0f0f0',
    padding: '20px',
    borderRadius: '8px',
    marginBottom: '30px',
    minHeight: '60px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '2px solid #ddd'
  },
  wordsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
    marginBottom: '30px',
    justifyContent: 'center'
  },
  wordButton: {
    padding: '12px 16px',
    background: '#007bff',
    color: 'white',
    border: 'none',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'background 0.2s'
  },
  buttonGroup: {
    display: 'flex',
    gap: '10px',
    justifyContent: 'center'
  },
  actionButton: {
    padding: '12px 20px',
    background: '#6c757d',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'background 0.2s'
  }
}
